import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  CircularProgress,
  Collapse,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Alert,
  useTheme,
  Stack,
} from '@mui/material';
import { ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { Turnstile } from '@marsidev/react-turnstile';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { AuthMethod } from 'types';
import {
  FacebookLogin,
  GoogleLogin,
  LoadingButton,
  TextDivider,
} from 'components';
import { secretManager } from 'services/api/config';
import { analytics, events } from 'services/analytics';
import { locations } from 'services/routes/locations';
import { useAuthStore, useFacebookLogin, useGoogleLogin } from 'services/hooks';
import FormHeader from 'pages/FormContainer/components/FormHeader';
import FormContainer from 'pages/FormContainer';
import FormFooter from 'pages/FormContainer/components/FormFooter';
import { displayVariant } from 'components/Login';
import useValidationSchema from './services/useValidationSchema';

const RegistrationForm = ({
  onSuccess,
}: {
  onSuccess: (method: AuthMethod) => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const { authStore, registerAndLogin, isRegisteringLoading, registerError } =
    useAuthStore();
  const { isLoggedIn } = authStore;

  const authMethod = useRef<AuthMethod>(AuthMethod.Email);

  const {
    loading: googleLoginLoading,
    error: googleLoginError,
    loginWithGoogle,
  } = useGoogleLogin();

  const {
    loading: facebookLoginLoading,
    error: facebookLoginError,
    loginWithFacebook,
  } = useFacebookLogin();

  const [turnstileValidationPassed, setTurnstileValidationPassed] =
    useState(false);

  const [isCheckingEmailAvailability, setIsCheckingEmailAvailability] =
    useState(false);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const registerSchema = useValidationSchema(
    () => {
      setIsCheckingEmailAvailability(true);
    },
    () => {
      setIsCheckingEmailAvailability(false);
    }
  );

  const { register, handleSubmit, formState, setValue, watch, clearErrors } =
    useForm<{
      password: string;
      email: string;
      cloudflareToken: string;
    }>({
      mode: 'onBlur',
      resolver: yupResolver(registerSchema),
      defaultValues: { password: '' },
    });
  const { errors, isValid } = formState;

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const setAuthMethod = (method: AuthMethod) => {
    authMethod.current = method;
  };

  const trackEvent = (method: AuthMethod) => {
    analytics.track(events.register(method));
  };

  const onSubmit = (data: {
    email: string;
    password: string;
    cloudflareToken: string;
  }) => {
    if (!isValid) return;

    // To log in iubenda DB
    const consentPreference = { general: true };
    registerAndLogin(data, consentPreference);
    // @ts-ignore
    trackEvent(authMethod);
  };

  const hasError = () =>
    !!registerError || !!googleLoginError || !!facebookLoginError;

  const getErrorMessage = useCallback(() => {
    if (registerError) {
      return registerError.message;
    }
    if (googleLoginError) {
      return googleLoginError.message;
    }
    if (facebookLoginError) {
      return facebookLoginError.message;
    }
    return null;
  }, [registerError, googleLoginError, facebookLoginError]);

  useEffect(() => {
    if (isLoggedIn) {
      onSuccess(authMethod.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const errorAlertMessage = useRef<HTMLElement>(null);

  const loginQueryString = useMemo(() => {
    const redirectParam =
      searchParams.get('loginRedirect') || searchParams.get('redirect');

    if (!redirectParam) {
      return '';
    }

    return `?redirect=${redirectParam}`;
  }, [searchParams]);

  useEffect(() => {
    if (!errorAlertMessage.current) return;
    window.scrollTo(0, 0);
  }, [errorAlertMessage, getErrorMessage]);

  const inputStyle = useMemo(
    () => ({
      width: '100%',
      '& input:-webkit-autofill': {
        '-webkit-background-clip': 'text',
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-webkit-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
      '& input:-moz-autofill': {
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-moz-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
      '& input:-ms-autofill': {
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-ms-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
    }),
    [theme]
  );

  return (
    <FormContainer>
      <Box
        sx={{
          maxWidth: theme.spacing(48.75),
          minHeight: '100%',
        }}
      >
        <Collapse in={hasError()} ref={errorAlertMessage}>
          <Alert
            variant="outlined"
            severity="error"
            color="error"
            sx={{
              marginTop: theme.spacing(5),
              [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(6),
                color: theme.palette.grey[50],
                fontSize: 12,
              },
            }}
            data-testid="registrationErrorAlert"
            icon={<ErrorOutline color="error" />}
          >
            {hasError() && getErrorMessage()}
          </Alert>
        </Collapse>

        <FormHeader
          title={t('auth:register')}
          description={t('auth:existingAccountText')}
          linkText={t('auth:logIn')}
          linkURL={locations.general.login.pathname + loginQueryString}
        />

        <Box
          component="form"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box>
            <Grid
              sx={{
                marginBottom: 2,
                overflow: 'hidden',
                borderRadius: theme.spacing(2.5),
              }}
            >
              <GoogleLogin
                handleSuccess={(token: string) => {
                  loginWithGoogle(token);
                  trackEvent(AuthMethod.Google);
                }}
              />
            </Grid>
            <Grid>
              <FacebookLogin
                variant={displayVariant.REGULAR}
                loading={facebookLoginLoading}
                handleSuccess={(token: string) => {
                  loginWithFacebook(token);
                  trackEvent(AuthMethod.Facebook);
                }}
              />
            </Grid>
          </Box>

          <TextDivider />

          <Stack gap={theme.spacing(3)} width="100%" alignItems="center">
            <TextField
              sx={inputStyle}
              error={!!errors.email}
              {...register('email', {
                onChange: () => {
                  clearErrors('email');
                },
              })}
              helperText={errors.email && errors.email.message}
              label={t('fields:email')}
              variant="outlined"
              id="email"
              InputProps={{
                endAdornment: isCheckingEmailAvailability ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
              type="email"
            />
            <TextField
              sx={inputStyle}
              type={isPasswordVisible ? 'text' : 'password'}
              error={!!errors.password}
              {...register('password', {
                onChange: (event) => {
                  clearErrors('password');
                  setValue('password', event.target.value);
                },
              })}
              helperText={errors.password && errors.password.message}
              value={watch('password')}
              label={t('fields:password')}
              variant="outlined"
              placeholder={t('auth:passwordPlaceholder')}
              id="password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} edge="end">
                      {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Turnstile
              options={{
                theme: 'dark',
              }}
              siteKey={secretManager.turnstileSiteKey}
              onSuccess={(token: string) => {
                setValue('cloudflareToken', token);
                setTurnstileValidationPassed(true);
              }}
              onError={() => setTurnstileValidationPassed(false)}
            />
            <LoadingButton
              type="submit"
              variant="rounded"
              fullWidth
              disabled={
                !turnstileValidationPassed ||
                googleLoginLoading ||
                facebookLoginLoading
              }
              loading={isRegisteringLoading}
              data-testid="submitButtonRegistration"
              onClick={() => setAuthMethod(AuthMethod.Email)}
            >
              {t('auth:startForFree')}
            </LoadingButton>
          </Stack>
        </Box>

        <FormFooter text={t('auth:noCreditCard')} />
      </Box>
    </FormContainer>
  );
};

export default RegistrationForm;
